import { Component, OnInit, Inject, ViewChild, HostListener } from "@angular/core";
import { Country } from "../models/country";
import { UserRoleEnum, Constant } from "../core/constant";
import { AdminService } from "../services/admin.service";
import { StandardService } from "../services/standard.service";
import { SupplierList } from "../models/supplier-list";
import { Supplier } from "../models/supplier";
import { CryptographyService } from "../services/cryptography.service";
import { WebStorageService, LOCAL_STORAGE } from "ngx-webstorage-service";
import { GlobalMarketSupplier } from "../models/global-market-supplier";
import { NgbModal, NgbModalOptions } from "@ng-bootstrap/ng-bootstrap";
import { FailModuleComponent } from "../fail-module/fail-module.component";
import { Router } from "@angular/router";
import { MatMenuTrigger } from "@angular/material/menu";
import { DeleteDialogComponent } from "../delete-dialog/delete-dialog.component";
import { sortWithSelectedFirst, shouldShowSelectedDivider } from "../core/filter-utils";

@Component({
  selector: "app-view-all-supplier-list",
  templateUrl: "./view-all-supplier-list.component.html",
  styleUrls: ["./view-all-supplier-list.component.scss"],
})
export class ViewAllSupplierListComponent implements OnInit {
  activePopupId: string | number | null = null;
  totalPages: number = 0;
  loading: boolean = false;
  userRole: number;
  userId: number;
  countryId: number = 0;
  countries: Country[] = [];
  filteredCountries: Country[] = [];
  selectedCountryIds: number[] = [];
  tempSelectedCountryIds: number[] = [];
  isCountryDropdownOpen: boolean = false;
  countrySearchText: string = "";

  pageSizes: any[] = Constant.PageSizes;
  searchKey: string = "";

  page: number = 1;
  pageSize: number = 10;
  collectionSize: number = 0;

  supplierList: SupplierList = new SupplierList();
  error: string = "";
  modalOption: NgbModalOptions = {};

  @ViewChild(MatMenuTrigger, { static: false }) trigger: MatMenuTrigger;

  get userRoleEnum() {
    return UserRoleEnum;
  }

  constructor(
    private adminService: AdminService,
    private standardService: StandardService,
    private cryptographyService: CryptographyService,
    @Inject(LOCAL_STORAGE)
    private storage: WebStorageService,
    private ngbModal: NgbModal,
    private router: Router
  ) {}

  ngOnInit() {
    this.userRole = this.cryptographyService.decryptData(
      this.storage.get("userRole")
    );
    this.userId = this.cryptographyService.decryptData(
      this.storage.get("userId")
    );
    this.page = this.standardService.supplierListFiltration.startingIndex || 1;
    this.pageSize =
      this.standardService.supplierListFiltration.pageSize || this.pageSize;
    this.searchKey = this.standardService.supplierListFiltration.searchKey || "";
    this.selectedCountryIds = this.standardService.supplierListFiltration
      .countryId?.length
      ? [...this.standardService.supplierListFiltration.countryId]
      : [];
    this.tempSelectedCountryIds = [...this.selectedCountryIds];
    this.getCountries();
    this.getSuppliers();
  }

  get displayCountryCount(): number {
    return this.isCountryDropdownOpen
      ? this.tempSelectedCountryIds.length
      : this.selectedCountryIds.length;
  }

  getCountryName(id: number): string {
    const country = this.countries.find((c) => c.id === id);
    return country ? country.name : "";
  }

  getCountryDisplayText(): string {
    const ids = this.isCountryDropdownOpen
      ? this.tempSelectedCountryIds
      : this.selectedCountryIds;

    if (!ids || ids.length === 0) {
      return "Country";
    }

    const firstCountryName = this.getCountryName(ids[0]);
    if (ids.length === 1) {
      return `Country: ${firstCountryName}`;
    }

    return `Country: ${firstCountryName} +${ids.length - 1}`;
  }

  getAssignedMarketsText(item: any): string {
    if (!item || !item.supplier) return "-";
    const supplier = item.supplier;

    let marketNames: string[] = [];

    if (supplier.assignedMarkets && supplier.assignedMarkets.length > 0) {
      marketNames = supplier.assignedMarkets;
    } else if (supplier.marketSuppliers && supplier.marketSuppliers.length > 0) {
      marketNames = supplier.marketSuppliers
        .map((ms: any) => (ms.country ? ms.country.name : this.getCountryName(ms.countryId)))
        .filter((n: string) => !!n);
    } else if (supplier.countryId && supplier.countryId.length > 0) {
      marketNames = supplier.countryId
        .map((id: number) => this.getCountryName(id))
        .filter((n: string) => !!n);
    }

    if (!marketNames || marketNames.length === 0) {
      return "-";
    }

    if (marketNames.length === 1) {
      return marketNames[0];
    } else if (marketNames.length === 2) {
      return marketNames.join(", ");
    } else {
      const firstTwo = marketNames.slice(0, 2).join(", ");
      const countRemaining = marketNames.length - 2;
      return `${firstTwo} +${countRemaining}`;
    }
  }

  getCountries(): void {
    this.standardService
      .GetMasterDataForAddNrbes(this.userRole, this.userId)
      .subscribe((data: any) => {
        if (data && data.countries) {
          this.countries = (data.countries || []).filter(
            (c: any) =>
              c.name !== "All Countries" &&
              c.name !== "All" &&
              c.id !== 0
          );
          this.filterCountries();
        }
      });
  }

  toggleCountryDropdown(event?: MouseEvent): void {
    if (event) {
      event.stopPropagation();
    }
    if (!this.isCountryDropdownOpen) {
      this.tempSelectedCountryIds = [...this.selectedCountryIds];
      this.filterCountries();
    }
    this.isCountryDropdownOpen = !this.isCountryDropdownOpen;
  }

  filterCountries(): void {
    let list = this.countries;
    if (this.countrySearchText) {
      const search = this.countrySearchText.toLowerCase();
      list = this.countries.filter((c) =>
        c.name.toLowerCase().includes(search)
      );
    }
    this.filteredCountries = sortWithSelectedFirst(list, (id) =>
      this.tempSelectedCountryIds.includes(id)
    );
  }

  showFilterDivider(index: number): boolean {
    return shouldShowSelectedDivider(this.filteredCountries, index, (id) =>
      this.tempSelectedCountryIds.includes(id)
    );
  }

  isCountrySelected(countryId: number): boolean {
    return this.tempSelectedCountryIds.includes(countryId);
  }

  isAllCountriesSelected(): boolean {
    return this.tempSelectedCountryIds.length === 0;
  }

  onAllCountriesChange(event: any): void {
    if (event.checked) {
      this.tempSelectedCountryIds = [];
    }
    this.filterCountries();
  }

  onCountryFilterChange(event: any, countryId: number): void {
    if (event.checked) {
      if (!this.tempSelectedCountryIds.includes(countryId)) {
        this.tempSelectedCountryIds.push(countryId);
      }
    } else {
      this.tempSelectedCountryIds = this.tempSelectedCountryIds.filter(
        (id) => id !== countryId
      );
    }
    this.filterCountries();
  }

  applyCountryFilter(): void {
    this.selectedCountryIds = [...this.tempSelectedCountryIds];
    this.standardService.supplierListFiltration.countryId = [
      ...this.selectedCountryIds,
    ];
    this.page = 1;
    this.getSuppliers();
    this.isCountryDropdownOpen = false;
  }

  removeCountryFilter(event: MouseEvent): void {
    event.stopPropagation();
    this.selectedCountryIds = [];
    this.tempSelectedCountryIds = [];
    this.standardService.supplierListFiltration.countryId = [];
    this.page = 1;
    this.getSuppliers();
  }

  selectedItemsPerPageChange(event) {
    this.page = 1;
    this.pageSize = event.value;
    this.standardService.supplierListFiltration.pageSize = this.pageSize;
    this.getSuppliers();
  }

  getSuppliers(): void {
    this.loading = true;
    this.standardService.supplierListFiltration.startingIndex = this.page;
    const countryIds =
      this.selectedCountryIds.length > 0 ? this.selectedCountryIds : null;
    this.adminService
      .GetGobalSupplierListWithSelectedMarketStatus(
        (this.page - 1) * this.pageSize,
        this.pageSize,
        countryIds,
        this.searchKey
      )
      .subscribe(
        (data: SupplierList) => {
          this.supplierList.globalMarketSuppliers =
            data.globalMarketSuppliers ? data.globalMarketSuppliers.map((o) => new GlobalMarketSupplier(o)) : [];
          this.collectionSize = data.count || this.supplierList.globalMarketSuppliers.length;
          this.totalPages = Math.ceil(this.collectionSize / this.pageSize);
          this.loading = false;
        },
        (error: string) => {
          this.error = error;
          const modalRef = this.ngbModal.open(FailModuleComponent);
          modalRef.componentInstance.errorMessage = error;
          this.loading = false;
        }
      );
  }

  onAddEditSupplier(supplierId: number = null) {
    this.activePopupId = null;
    if (!supplierId) {
      this.router.navigate(["/add-supplier"]);
    } else {
      this.router.navigate(["/add-supplier/" + supplierId]);
    }
  }

  togglePopup(event: MouseEvent, item: any): void {
    event.stopPropagation();
    const id = item.supplier ? item.supplier.id : item.id;
    this.activePopupId = this.activePopupId === id ? null : id;
  }

  onEdit(item: any, event?: MouseEvent): void {
    event?.stopPropagation();
    this.activePopupId = null;
    const supplierId = item.supplier ? item.supplier.id : item.id;
    this.onAddEditSupplier(supplierId);
  }

  closeMatMenuAndOpenDeleteModal(item, event) {
    this.trigger?.closeMenu();
    setTimeout(() => {
      this.openDeleteModal(item, event);
    }, 100);
  }

  openDeleteModal(item, event) {
    event.stopPropagation();
    this.activePopupId = null;
    const modalRef = this.ngbModal.open(DeleteDialogComponent);
    modalRef.componentInstance.title =
      "Do you want to remove this supplier from the list? ";
    modalRef.componentInstance.deleteItem = item;
    modalRef.componentInstance.message =
      "All the dependencies will be removed along with this Supplier";
    this.trigger?.closeMenu();
    modalRef.componentInstance.deletePopupEmit.subscribe((deleteItem) => {
      if (deleteItem != null) {
        let supplier = new Supplier(item.supplier);
        this.loading = true;
        this.adminService.DeleteSupplier(supplier).subscribe((data: any) => {
          this.loading = false;
          this.page = 1;
          this.getSuppliers();
        });
      }
      this.ngbModal.dismissAll();
    });
  }

  @HostListener("document:click", ["$event"])
  onClickOutside(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (!target.closest(".grid-actions")) {
      this.activePopupId = null;
    }
    if (!target.closest(".country-dropdown-wrapper")) {
      this.isCountryDropdownOpen = false;
    }
  }

  addSearchKey(searchKey: any) {
    if (typeof searchKey === "string") {
      this.searchKey = searchKey;
    } else if (searchKey && searchKey.value !== undefined) {
      this.searchKey = searchKey.value;
    }
    this.standardService.supplierListFiltration.searchKey = this.searchKey;
    this.page = 1;
    this.getSuppliers();
  }

  rowSelected(item) {
    this.router.navigate(["/supplier-detail-view/" + item.supplier.id]);
  }

  goToNextPage() {
    if (this.page < this.totalPages) {
      this.page++;
      this.getSuppliers();
    }
  }

  goToPreviousPage() {
    if (this.page > 1) {
      this.page--;
      this.getSuppliers();
    }
  }
}


